(() => {
  'use strict';
  function roomCaption(mediaId, roomIds, label) {
    if (Array.isArray(roomIds)) return roomIds.some(id => String(id) === String(mediaId)) ? label : '';
    const key = String(mediaId);
    return roomIds && typeof roomIds === 'object' && Object.hasOwn(roomIds, key) && typeof roomIds[key] === 'string' ? roomIds[key] : '';
  }
  function fitDimensions(imageWidth, imageHeight, availableWidth, availableHeight) {
    const values = [imageWidth, imageHeight, availableWidth, availableHeight].map(Number);
    if (values.some(value => !Number.isFinite(value) || value <= 0)) return null;
    const [width, height, roomWidth, roomHeight] = values;
    const scale = Math.min(roomWidth / width, roomHeight / height, 1);
    return {width:Math.floor(width * scale * 1000) / 1000, height:Math.floor(height * scale * 1000) / 1000};
  }
  function galleryStageHeight(imageWidth, imageHeight, stageWidth, horizontalPadding, verticalPadding) {
    const values = [imageWidth, imageHeight, stageWidth, horizontalPadding, verticalPadding].map(Number);
    if (values.some(value => !Number.isFinite(value)) || values.slice(0,3).some(value => value <= 0) || values.slice(3).some(value => value < 0)) return null;
    const [width, height, roomWidth, padX, padY] = values;
    if (roomWidth <= padX) return null;
    return Math.ceil(((roomWidth - padX) * height / width + padY) * 1000) / 1000;
  }
  if (typeof module === 'object' && module.exports) module.exports = {roomCaption, fitDimensions, galleryStageHeight};
  if (typeof window === 'undefined' || customElements.get('aroha-relief-gallery-tools')) return;
  class ReliefGalleryTools extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('.product__media-wrapper')?.querySelector('media-gallery');
      this.caption = this.querySelector('[data-relief-gallery-caption]');
      try { this.roomIds = JSON.parse(this.querySelector('[data-relief-room-media]')?.textContent || '[]'); }
      catch (_) { this.roomIds = []; }
      this.onChange = () => { this.updateGalleryCaption(); this.sizeGalleryStage(); };
      this.onGalleryResize = () => this.sizeGalleryStage();
      this.onGalleryLoad = () => this.sizeGalleryStage();
      this.gallery?.addEventListener('load', this.onGalleryLoad, true);
      window.addEventListener('resize', this.onGalleryResize);
      if (typeof ResizeObserver === 'function' && this.gallery) {
        this.galleryResizeObserver = new ResizeObserver(this.onGalleryResize);
        this.galleryResizeObserver.observe(this.gallery);
      }
      this.galleryObserver = new MutationObserver(this.onChange);
      if (this.gallery) this.galleryObserver.observe(this.gallery, {subtree:true, childList:true, attributes:true, attributeFilter:['class']});
      this.updateGalleryCaption();
      this.sizeGalleryStage();
      customElements.whenDefined('product-modal').then(() => {
        if (!this.isConnected) return;
        this.modal = document.getElementById('ProductModal-' + this.dataset.sectionId);
        if (!this.modal?.classList.contains('aa-relief-modal')) return;
        this.content = this.modal.querySelector('.product-media-modal__content');
        this.toggle = this.modal.querySelector('[data-relief-detail-toggle]');
        this.modalCaption = this.modal.querySelector('[data-relief-modal-caption]');
        this.onDetail = () => this.setDetail(!this.modal.classList.contains('aa-relief-detail'));
        this.toggle?.addEventListener('click', this.onDetail);
        this.onResize = () => this.fitActiveImage();
        this.onImageLoad = event => { if (event.target?.matches('img.active')) this.fitActiveImage(); };
        this.content?.addEventListener('load', this.onImageLoad, true);
        window.addEventListener('resize', this.onResize);
        window.visualViewport?.addEventListener('resize', this.onResize);
        if (typeof ResizeObserver === 'function' && this.content) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.content);
        }
        this.modalObserver = new MutationObserver(records => {
          if (records.some(record => record.target === this.modal && record.attributeName === 'open'
            || this.content?.contains(record.target))) this.resetView();
        });
        this.modalObserver.observe(this.modal, {subtree:true, childList:true, attributes:true, attributeFilter:['open','class']});
        this.resetView();
      });
    }
    disconnectedCallback() {
      this.galleryObserver?.disconnect();
      this.galleryResizeObserver?.disconnect();
      this.gallery?.removeEventListener('load', this.onGalleryLoad, true);
      window.removeEventListener('resize', this.onGalleryResize);
      this.modalObserver?.disconnect();
      this.toggle?.removeEventListener('click', this.onDetail);
      this.content?.removeEventListener('load', this.onImageLoad, true);
      this.resizeObserver?.disconnect();
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
        window.visualViewport?.removeEventListener('resize', this.onResize);
      }
    }
    writeCaption(target, mediaId) {
      if (!target) return;
      const text = roomCaption(mediaId, this.roomIds, this.dataset.roomLabel || '');
      if (target.textContent !== text) target.textContent = text;
      target.hidden = !text;
    }
    updateGalleryCaption() {
      const target = this.gallery?.querySelector('.product__media-item.is-active')?.dataset.mediaId;
      const prefix = this.dataset.sectionId + '-';
      this.writeCaption(this.caption, target?.startsWith(prefix) ? target.slice(prefix.length) : '');
    }
    sizeGalleryStage() {
      const item = this.gallery?.querySelector('.product__media-item.is-active');
      const stage = item?.querySelector('.product-media-container');
      const image = stage?.querySelector('img');
      if (!stage || !image) return;
      if (!window.matchMedia('(max-width:989px)').matches) {
        if (stage.style.height) stage.style.removeProperty('height');
        return;
      }
      const style = window.getComputedStyle(image);
      const horizontal = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const vertical = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const height = galleryStageHeight(image.naturalWidth || image.getAttribute('width'), image.naturalHeight || image.getAttribute('height'), stage.clientWidth, horizontal, vertical);
      if (height !== null && stage.style.height !== height + 'px') stage.style.height = height + 'px';
    }
    fitActiveImage() {
      if (!this.content || this.modal?.classList.contains('aa-relief-detail')) return;
      const image = this.content.querySelector('img.active');
      if (!image) return;
      const style = window.getComputedStyle(this.content);
      const width = this.content.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
      const height = this.content.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
      const size = fitDimensions(image.naturalWidth || image.getAttribute('width'), image.naturalHeight || image.getAttribute('height'), width, height);
      if (!size) return;
      image.style.width = size.width + 'px';
      image.style.height = size.height + 'px';
    }
    setDetail(detail) {
      if (!this.modal || !this.toggle) return;
      if (this.modal.classList.contains('aa-relief-detail') !== detail) this.modal.classList.toggle('aa-relief-detail', detail);
      const pressed = String(detail);
      if (this.toggle.getAttribute('aria-pressed') !== pressed) this.toggle.setAttribute('aria-pressed', pressed);
      const text = detail ? this.dataset.fitLabel : this.dataset.detailLabel;
      if (this.toggle.textContent !== text) this.toggle.textContent = text;
      const image = this.content?.querySelector('img.active');
      if (detail && image) { image.style.removeProperty('width'); image.style.removeProperty('height'); }
      else this.fitActiveImage();
      if (this.content) { this.content.scrollTop = 0; this.content.scrollLeft = 0; }
    }
    resetView() {
      this.writeCaption(this.modalCaption, this.content?.querySelector('[data-media-id].active')?.dataset.mediaId);
      this.setDetail(false);
    }
  }
  customElements.define('aroha-relief-gallery-tools', ReliefGalleryTools);
})();
